import { PermissionedRouteObject } from "@redotech/redo-merchant-app-common/routing/protected-routes";
import { DEFAULT_CATALOG_VIEW } from "@redotech/redo-model/views/default-catalog-products-views";
import { breadcrumbSlot } from "@redotech/redo-web/breadcrumb";
import { Navigate } from "react-router-dom";

const catalogProductsViewBreadcrumb = breadcrumbSlot();
const productFamilyDetailBreadcrumb = breadcrumbSlot();
const productDetailBreadcrumb = breadcrumbSlot();

const aeoEnabledPermission = ({
  team,
}: {
  team: { settings: { aeo?: { enabled?: boolean } } };
}) => {
  return team.settings.aeo?.enabled === true;
};

export const aeoRoutes: PermissionedRouteObject[] = [
  {
    path: "",
    handle: { breadcrumb: "AEO", permission: aeoEnabledPermission },
    async lazy() {
      const { Page } = await import("../../page");
      return {
        element: (
          <Page
            hidePadding={(url: string) =>
              ["dashboard", "prompts"].some((path) => url.includes(path))
            }
          />
        ),
      };
    },
    children: [
      { index: true, element: <Navigate replace to="dashboard" /> },
      {
        path: "dashboard",
        handle: { breadcrumb: "Dashboard" },
        async lazy() {
          const { AeoDashboard } = await import("../../../aeo/dashboard");
          return { Component: AeoDashboard };
        },
      },
      {
        path: "prompts",
        handle: { breadcrumb: "Prompts" },
        children: [
          {
            index: true,
            async lazy() {
              const { AeoPromptsPage } = await import("../../../aeo/prompts");
              return { Component: AeoPromptsPage };
            },
          },
          {
            path: ":promptId",
            handle: { breadcrumb: "Prompt Details" },
            async lazy() {
              const { PromptDetailLayout } = await import(
                "../../../aeo/prompts/prompt-detail-layout"
              );
              return { Component: PromptDetailLayout };
            },
            children: [
              {
                index: true,
                async lazy() {
                  const { AeoPromptDetailPage } = await import(
                    "../../../aeo/prompts/prompt-detail-page"
                  );
                  return { Component: AeoPromptDetailPage };
                },
              },
            ],
          },
        ],
      },
      {
        path: "configure",
        handle: { breadcrumb: "Configure" },
        async lazy() {
          const { AeoConfigureLayout } = await import(
            "../../../aeo/configure/configure-layout"
          );
          return { Component: AeoConfigureLayout };
        },
        children: [
          { index: true, element: <Navigate replace to="overview" /> },
          {
            path: "overview",
            async lazy() {
              const { AeoConfigureOverviewPage } = await import(
                "../../../aeo/configure/overview-page"
              );
              return { Component: AeoConfigureOverviewPage };
            },
          },
          {
            path: "competition",
            async lazy() {
              const { AeoConfigureCompetitionPage } = await import(
                "../../../aeo/configure/competition-page"
              );
              return { Component: AeoConfigureCompetitionPage };
            },
          },
          {
            path: "integrations",
            async lazy() {
              const { AeoConfigureIntegrationsPage } = await import(
                "../../../aeo/configure/integrations-page"
              );
              return { Component: AeoConfigureIntegrationsPage };
            },
          },
        ],
      },
      {
        path: "catalog",
        handle: { breadcrumb: "Catalog" },
        children: [
          { index: true, element: <Navigate replace to="products" /> },
          {
            path: "products",
            handle: { breadcrumb: "Products" },
            children: [
              {
                index: true,
                element: <Navigate replace to={DEFAULT_CATALOG_VIEW} />,
              },
              {
                path: ":viewName",
                handle: { breadcrumb: catalogProductsViewBreadcrumb },
                async lazy() {
                  const { CatalogProductsWrapper } = await import(
                    "@redotech/redo-merchant-inventory-app/catalog/catalog-products-wrapper"
                  );
                  return {
                    element: (
                      <CatalogProductsWrapper
                        breadcrumb={catalogProductsViewBreadcrumb}
                        productBreadcrumb={productDetailBreadcrumb}
                        productFamilyBreadcrumb={productFamilyDetailBreadcrumb}
                      />
                    ),
                  };
                },
                children: [
                  {
                    index: true,
                    async lazy() {
                      const { CatalogProducts } = await import(
                        "@redotech/redo-merchant-inventory-app/catalog-products"
                      );
                      return { Component: CatalogProducts };
                    },
                  },
                  {
                    path: ":productFamilyId",
                    handle: { breadcrumb: productFamilyDetailBreadcrumb },
                    children: [
                      {
                        index: true,
                        async lazy() {
                          const { ProductFamilyView } = await import(
                            "@redotech/redo-merchant-inventory-app/product-families/product-family-view"
                          );
                          return { Component: ProductFamilyView };
                        },
                      },
                      {
                        path: ":productId",
                        handle: { breadcrumb: productDetailBreadcrumb },
                        children: [
                          {
                            index: true,
                            async lazy() {
                              const { ProductView } = await import(
                                "@redotech/redo-merchant-inventory-app/products/product-view"
                              );
                              return { Component: ProductView };
                            },
                          },
                          {
                            path: "locations/:locationId",
                            handle: { breadcrumb: "Adjustment History" },
                            async lazy() {
                              const { AdjustmentHistory } = await import(
                                "@redotech/redo-merchant-inventory-app/adjustment-history/adjustment-history"
                              );
                              return { Component: AdjustmentHistory };
                            },
                          },
                        ],
                      },
                    ],
                  },
                ],
              },
            ],
          },
        ],
      },
    ],
  },
];
